// filepath: apps/web/src/features/admin/webhooks/EndpointFormDialog.tsx
//
// FR-UI-ADM-6 AC-2 -- the create form dialog. Renders two
// screens:
//
//   1. Form screen: {url, process_def_id} fields with focus
//      trap + aria-labelledby + per-field aria-describedby.
//      Submit fires useCreateWebhookEndpoint.
//
//   2. Success screen: after a 201, renders the one-time
//      copy-to-clipboard affordance for the `key_value` field
//      (INV-5). The prompt "Copy this value now — you will not
//      be able to see it again." renders verbatim via
//      useTranslation(). On dialog close, the key_value is
//      destroyed from React state via setKeyValue(null).
//
// The dialog never serializes key_value into the audit log,
// the console, or anywhere else outside this component's local
// state (INV-5 / FR-SCR-8 contract).

import React, { useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import type { CreateWebhookEndpointRequest } from "./types";

interface EndpointFormDialogProps {
  onClose: () => void;
  onSubmit: (req: CreateWebhookEndpointRequest) => Promise<void> | void;
  isSubmitting: boolean;
  errorMessage: string | null;
  /**
   * Controlled key-value return from the create POST. When
   * non-null the dialog renders the one-time copy-to-clipboard
   * success screen (INV-5). The page passes the response body
   * through this prop; the dialog NEVER controls its own
   * key-value state because the create response is owned by
   * the page-level mutation hook. The previous design had the
   * dialog manage key_value locally — that path was a
   * production defect: the page closed the dialog immediately
   * on mutation success, so the dialog's internal state never
   * reached the success screen. Caught by
   * WebhookEndpointsPage.test.tsx AC-2.
   * (ISS-f4fa1b7b-205)
   */
  successKeyValue?: string | null;
}

export function EndpointFormDialog({
  onClose,
  onSubmit,
  isSubmitting,
  errorMessage,
  successKeyValue = null,
}: EndpointFormDialogProps): React.ReactElement {
  const { t } = useTranslation();
  const [url, setUrl] = useState("");
  const [processDefID, setProcessDefID] = useState("");
  const [copied, setCopied] = useState(false);

  // Wrap onClose so closing the dialog wipes the key_value
  // from React state + DOM (INV-5 hard guarantee). The
  // parent should clear its `successKeyValue` prop in
  // response to onClose; the dialog does not manage
  // key_value state locally.
  const handleClose = (): void => {
    setCopied(false);
    onClose();
  };

  if (successKeyValue !== null && successKeyValue !== undefined) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-success-title"
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        data-testid="create-success-dialog"
      >
        <div className="w-full max-w-md rounded-md border border-border bg-background p-4 shadow-lg">
          <h2
            id="create-success-title"
            className="text-sm font-semibold"
            data-testid="create-success-title"
          >
            {t("admin.webhooks.dialog.create.success.title")}
          </h2>
          <p className="mt-2 text-xs text-muted-foreground">
            {t("admin.webhooks.dialog.create.success.copy_key_value")}
          </p>
          <div className="mt-3 flex items-center gap-2">
            <input
              type="text"
              readOnly
              value={successKeyValue}
              aria-label={t("admin.webhooks.dialog.create.success.title")}
              className="w-full rounded border border-border bg-muted px-2 py-1 font-mono text-xs"
              data-testid="key-value-readonly"
            />
            <button
              type="button"
              className="rounded bg-primary px-3 py-1 text-xs font-medium text-primary-foreground"
              onClick={() => {
                if (
                  typeof navigator !== "undefined" &&
                  navigator.clipboard !== undefined
                ) {
                  void navigator.clipboard
                    .writeText(successKeyValue)
                    .then(() => {
                      setCopied(true);
                    });
                }
              }}
              data-testid="key-value-copy"
            >
              {copied
                ? t("admin.webhooks.dialog.create.success.copied")
                : t("common.copy")}
            </button>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              className="rounded border border-border px-3 py-1 text-xs"
              onClick={handleClose}
              data-testid="create-success-close"
            >
              {t("common.close")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-dialog-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="endpoint-form-dialog"
    >
      <div className="w-full max-w-md rounded-md border border-border bg-background p-4 shadow-lg">
        <h2
          id="create-dialog-title"
          className="text-sm font-semibold"
          data-testid="create-dialog-title"
        >
          {t("admin.webhooks.dialog.create.title")}
        </h2>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await onSubmit({ url, process_def_id: processDefID });
            } catch {
              // errorMessage below carries the failure
            }
          }}
          className="mt-3 space-y-3"
        >
          <div>
            <label htmlFor="endpoint-url" className="block text-xs font-medium">
              {t("admin.webhooks.dialog.create.label.url")}
            </label>
            <input
              id="endpoint-url"
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              aria-describedby="endpoint-url-help"
              required
              className="mt-1 w-full rounded border border-border px-2 py-1 text-sm"
              data-testid="endpoint-url-input"
            />
            <p
              id="endpoint-url-help"
              className="mt-1 text-xs text-muted-foreground"
            >
              {"https://"}
            </p>
          </div>
          <div>
            <label
              htmlFor="endpoint-process-def"
              className="block text-xs font-medium"
            >
              {t("admin.webhooks.dialog.create.label.process_def")}
            </label>
            <input
              id="endpoint-process-def"
              type="text"
              value={processDefID}
              onChange={(e) => setProcessDefID(e.target.value)}
              required
              className="mt-1 w-full rounded border border-border px-2 py-1 text-sm"
              data-testid="endpoint-process-def-input"
            />
          </div>
          {errorMessage !== null && (
            <p
              className="text-xs text-destructive"
              role="alert"
              data-testid="endpoint-form-error"
            >
              {errorMessage}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="rounded border border-border px-3 py-1 text-xs"
              onClick={handleClose}
              disabled={isSubmitting}
              data-testid="endpoint-form-cancel"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              className="rounded bg-primary px-3 py-1 text-xs font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
              disabled={isSubmitting}
              data-testid="endpoint-form-submit"
            >
              {t("admin.webhooks.dialog.create.cta")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
